import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, getDocs, setDoc, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { FaPlus, FaTrash, FaMinus, FaEye, FaEyeSlash, FaGripVertical, FaCheck, FaRedo, FaEdit, FaArrowLeft } from 'react-icons/fa6';
import { FaTimes as FaTimesIcon } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate, useLocation } from 'react-router-dom';

const SurveyBuilder = ({ documentId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const surveyToEdit = location.state?.surveyToEdit;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([]);
  const [isPreview, setIsPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [triggerDateTime, setTriggerDateTime] = useState(null);
  const [isRepeating, setIsRepeating] = useState(false);
  const [repeatInterval, setRepeatInterval] = useState('daily');
  const [repeatDays, setRepeatDays] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '' });
  const [surveys, setSurveys] = useState([]);

  useEffect(() => {
    if (surveyToEdit) {
      setTitle(surveyToEdit.title || '');
      setDescription(surveyToEdit.description || '');
      setQuestions(surveyToEdit.questions || []);
      if (surveyToEdit.triggerDateTime) {
        setTriggerDateTime(new Date(surveyToEdit.triggerDateTime.seconds * 1000));
      }
      if (surveyToEdit.isRepeating) {
        setIsRepeating(true);
        setRepeatInterval(surveyToEdit.repeatInterval || 'daily');
        setRepeatDays(surveyToEdit.repeatDays || []);
      }
    }
  }, [surveyToEdit]);

  const addQuestion = () => {
    setQuestions([
      ...questions,
      { title: '', type: 'open', options: [''], index: questions.length + 1 }
    ]);
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    if (field === 'type' && value === 'open') {
      // Reset options if switching to open type
      updated[index].options = [''];
    }
    setQuestions(updated);
  };

  const handleOptionChange = (qIndex, oIndex, value) => {
    const updated = [...questions];
    updated[qIndex].options[oIndex] = value;
    setQuestions(updated);
  };

  const addOption = (qIndex) => {
    const updated = [...questions];
    updated[qIndex].options.push('');
    setQuestions(updated);
  };

  const removeOption = (qIndex, oIndex) => {
    const updated = [...questions];
    if (updated[qIndex].options.length > 1) {
      updated[qIndex].options.splice(oIndex, 1);
      setQuestions(updated);
    }
  };

  const removeQuestion = (index) => {
    const updated = [...questions];
    updated.splice(index, 1);
    setQuestions(updated);
    setValidationError('');
  };

  const validateSurvey = () => {
    if (!title.trim()) {
      setValidationError('Please enter a survey title.');
      return false;
    }

    if (!description.trim()) {
      setValidationError('Please enter a survey description.');
      return false;
    }

    if (questions.length === 0) {
      setValidationError('Add at least one question.');
      return false;
    }

    // if (!triggerDateTime) {
    //   setValidationError('Please select a trigger date and time.');
    //   return false;
    // }

    if (isRepeating && repeatInterval === 'weekly' && repeatDays.length === 0) {
      setValidationError('Please select at least one day for weekly repetition.');
      return false;
    }

    for (let q of questions) {
      if (!q.title.trim()) {
        setValidationError('All questions must have a title.');
        return false;
      }
      if ((q.type === 'close' || q.type === 'multi') && q.options.some(opt => !opt.trim())) {
        setValidationError('All multiple choice options must be filled.');
        return false;
      }
    }

    return true;
  };

  const submitSurvey = async () => {
    if (!validateSurvey()) return;

    setIsSubmitting(true);
    setValidationError('');

    try {
      const surveyData = {
        title,
        description,
        questions,
        triggerDateTime,
        isRepeating,
        repeatInterval,
        repeatDays: isRepeating && repeatInterval === 'weekly' ? repeatDays : [],
        updatedAt: serverTimestamp()
      };

      if (surveyToEdit) {
        // Update existing survey
        const surveyRef = doc(db, 'EMA', 'data', 'surveys', surveyToEdit.id);
        await updateDoc(surveyRef, surveyData);
        setSuccessMessage('Survey updated successfully!');
      } else {
        // Create new survey
        surveyData.createdAt = serverTimestamp();
        surveyData.surveyId = crypto.randomUUID();
        await setDoc(
          doc(db, "EMA", "data", "surveys", surveyData.surveyId), // Set doc with custom ID
          surveyData
        );
        setSuccessMessage('Survey created successfully!');
      }

      setTimeout(() => {
        setSuccessMessage('');
        navigate(-1);
      }, 1500);
    } catch (err) {
      console.error('Error saving survey:', err);
      setValidationError('Failed to save survey. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setQuestions(items);
  };

  const handleEdit = (survey) => {
    setEditingSurvey(survey);
    setFormData({ title: survey.title, description: survey.description });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, `EMA/${editingSurvey?.id}/surveys`, id));
      setSuccessMessage('Survey deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting survey:', err);
      setValidationError('Failed to delete survey. Please try again.');
    }
  };

  useEffect(() => {
    const fetchSurveys = async () => {
      const surveysCollection = collection(db, `EMA/${editingSurvey?.id}/surveys`);
      const surveysSnapshot = await getDocs(surveysCollection);
      const surveysData = surveysSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSurveys(surveysData);
    };

    if (editingSurvey) {
      fetchSurveys();
    }
  }, [editingSurvey]);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-start mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-10 h-10 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm"
          >
            <FaArrowLeft className="w-4 h-4" />
          </button>
        </div>

        {validationError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-100 rounded-lg">
            <p className="text-red-700">{validationError}</p>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-100 rounded-lg">
            <p className="text-green-700">{successMessage}</p>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <label htmlFor="title" className="block text-2xl font-bold text-gray-900 mb-4">
              Survey Title
            </label>
            <input
              id="title"
              type="text"
              placeholder="Enter survey title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setValidationError('');
              }}
              className="w-full px-6 py-4 text-xl border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="description" className="block text-xl font-semibold text-gray-900 mb-4">
              Survey Description
            </label>
            <textarea
              id="description"
              placeholder="Enter survey description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setValidationError('');
              }}
              rows={4}
              className="w-full px-6 py-4 text-lg border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
            />
          </div>

          {/* <div className="mb-6 p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-gray-900">Trigger Settings</h3>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={isRepeating}
                  onChange={(e) => {
                    setIsRepeating(e.target.checked);
                    if (!e.target.checked) {
                      setRepeatDays([]);
                      setRepeatInterval('daily');
                    }
                    setValidationError('');
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Repeat Survey</span>
              </label>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trigger Date and Time
              </label>
              <DatePicker
                selected={triggerDateTime}
                onChange={(date) => {
                  setTriggerDateTime(date);
                  setValidationError('');
                }}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="MMMM d, yyyy h:mm aa"
                minDate={new Date()}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholderText="Select date and time"
              />
            </div>

            {isRepeating && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Repeat Interval
                  </label>
                  <select
                    value={repeatInterval}
                    onChange={(e) => {
                      setRepeatInterval(e.target.value);
                      if (e.target.value !== 'weekly') {
                        setRepeatDays([]);
                      }
                      setValidationError('');
                    }}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                {repeatInterval === 'weekly' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Repeat on Days
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                        <button
                          key={day}
                          onClick={() => {
                            setRepeatDays(prev => 
                              prev.includes(index)
                                ? prev.filter(d => d !== index)
                                : [...prev, index]
                            );
                            setValidationError('');
                          }}
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            repeatDays.includes(index)
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div> */}
        </div>

        <div className="mb-6 p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="questions">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {questions.map((q, i) => (
                    <Draggable key={i} draggableId={`question-${i}`} index={i}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="mb-6 p-6 bg-white rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center gap-4 mb-4">
                            <input
                              type="text"
                              placeholder={`Question #${i + 1} Title`}
                              value={q.title}
                              onChange={(e) => {
                                handleQuestionChange(i, 'title', e.target.value);
                                setValidationError('');
                              }}
                              className="flex-grow px-4 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            />
                            <button
                              onClick={() => removeQuestion(i)}
                              className="shrink-0 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors bg-transparent hover:bg-transparent"
                              title="Remove question"
                            >
                              <FaTrash className="h-10 w-10" />
                            </button>
                          </div>

                          <select
                            value={q.type}
                            onChange={(e) => handleQuestionChange(i, 'type', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors mb-4"
                          >
                            <option value="open">Open Question (Free Text)</option>
                            <option value="close">Single Select (Choose One)</option>
                            <option value="multi">Multi Select (Choose Multiple)</option>
                          </select>

                          {(q.type === 'close' || q.type === 'multi') && (
                            <div className="space-y-3">
                              {q.options.map((opt, j) => (
                                <div key={j} className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    placeholder={`Option ${j + 1}`}
                                    value={opt}
                                    onChange={(e) => {
                                      handleOptionChange(i, j, e.target.value);
                                      setValidationError('');
                                    }}
                                    className="flex-grow px-4 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                  />
                                  <button
                                    onClick={() => removeOption(i, j)}
                                    className="shrink-0 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors bg-transparent hover:bg-transparent"
                                    title="Remove option"
                                  >
                                    <FaMinus className="h-10 w-10" />
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={() => addOption(i)}
                                className="mt-2 inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                              >
                                <FaPlus className="w-3 h-3 mr-1.5" />
                                Add Option
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <button
            onClick={addQuestion}
            className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <FaPlus className="w-4 h-4 mr-1.5" />
            Add Question
          </button>
        </div>

        <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FaTimesIcon className="w-4 h-4 mr-1.5" />
              Cancel
            </button>
            <button
              onClick={submitSurvey}
              disabled={isSubmitting}
              className={`flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {surveyToEdit ? 'Updating...' : 'Submitting...'}
                </>
              ) : (
                <>
                  <FaCheck className="w-4 h-4 mr-1.5" />
                  {surveyToEdit ? 'Update Survey' : 'Submit Survey'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyBuilder;
